<template>
  <div id="app">
    <Navbar />
    <main class="main-content">
      <router-view />
    </main>
    <Footer />
  </div>
</template>

<script>
import Navbar from '@/components/layout/Navbar.vue'
import Footer from '@/components/layout/Footer.vue'

export default {
  name: 'App',
  components: {
    Navbar,
    Footer
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css');

:root {
  /* 颜色 - 紫色系深色主题 */
  --primary-color: #9D6EFD; /* 亮紫色 */
  --primary-hover: #8B5CF6; /* 略深的紫色 */
  --primary-light: rgba(157, 110, 253, 0.15);
  --secondary-color: #C084FC; /* 亮淡紫色 */
  --accent-color: #E879F9; /* 粉紫色强调色 */
  
  /* 深色背景 */
  --bg-dark: #1F1A38; /* 主要深紫色背景 */
  --bg-darker: #15112B; /* 更深的紫色背景 */
  --bg-card: #2A2344; /* 卡片背景 */
  --bg-light: #312856; /* 稍亮一点的紫色背景 */
  --bg-white: #3A2F63; /* 替代白色的浅紫色 */
  
  /* 文字颜色 - 适应深色背景 */
  --text-primary: #FFFFFF;
  --text-secondary: #CEC4F2;
  --text-light: #9D94C8;
  
  /* 圆角 */
  --border-radius-sm: 12px;
  --border-radius-md: 20px;
  --border-radius-lg: 30px;
  --border-radius-xl: 100px;
  
  /* 阴影 - 深色主题 */
  --shadow-sm: 0 4px 12px rgba(0, 0, 0, 0.3);
  --shadow-md: 0 8px 24px rgba(0, 0, 0, 0.4);
  --shadow-lg: 0 12px 32px rgba(0, 0, 0, 0.5);
  
  /* 拟态设计阴影 - 深色主题 */
  --neumorphism-flat: 6px 6px 12px rgba(0, 0, 0, 0.3), -6px -6px 12px rgba(88, 73, 153, 0.1);
  --neumorphism-pressed: inset 4px 4px 8px rgba(0, 0, 0, 0.4), inset -4px -4px 8px rgba(88, 73, 153, 0.05);
  --neumorphism-light: 4px 4px 8px rgba(0, 0, 0, 0.2), -4px -4px 8px rgba(88, 73, 153, 0.05);
  
  /* 边框和其他 */
  --transition: all 0.3s ease;
  --border-light: 1px solid rgba(157, 110, 253, 0.2);
  
  /* 渐变 */
  --gradient-primary: linear-gradient(135deg, #9D6EFD 0%, #C084FC 100%);
  --gradient-secondary: linear-gradient(135deg, #8B5CF6 0%, #E879F9 100%);
  --gradient-light: linear-gradient(135deg, rgba(157, 110, 253, 0.3) 0%, rgba(232, 121, 249, 0.3) 100%);
  --gradient-dark: linear-gradient(135deg, rgba(31, 26, 56, 0.8) 0%, rgba(42, 35, 68, 0.8) 100%);
  --gradient-glass: linear-gradient(135deg, rgba(58, 47, 99, 0.4) 0%, rgba(42, 35, 68, 0.4) 100%);
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--text-primary);
  background-color: var(--bg-dark);
  line-height: 1.6;
}

.main-content {
  min-height: 100vh;
  padding-top: 80px;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

h1, h2, h3, h4, h5, h6 {
  font-weight: 700;
  line-height: 1.3;
}

a {
  text-decoration: none;
  color: var(--primary-color);
  transition: var(--transition);
}

button {
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  border: none;
  outline: none;
}

.section {
  padding: 6rem 0;
}

.section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.section-title {
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  color: var(--text-primary);
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.section-description {
  font-size: 1.2rem;
  color: var(--text-secondary);
  max-width: 700px;
  margin: 0 auto;
}

/* 通用按钮样式 */
.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: var(--border-radius-xl);
  font-weight: 600;
  font-size: 1rem;
  transition: var(--transition);
}

.button.is-primary {
  background: var(--gradient-primary);
  color: white;
  box-shadow: var(--shadow-sm);
}

.button.is-primary:hover {
  background: var(--gradient-secondary);
  transform: translateY(-3px);
  box-shadow: var(--shadow-md);
}

.button.is-outlined {
  background-color: rgba(255, 255, 255, 0.05);
  color: var(--text-primary);
  border: var(--border-light);
  box-shadow: var(--neumorphism-light);
}

.button.is-outlined:hover {
  transform: translateY(-3px);
  box-shadow: var(--shadow-sm);
  background-color: rgba(255, 255, 255, 0.1);
}

/* 拟态卡片 */
.neumorphic-card {
  background-color: var(--bg-card);
  border-radius: var(--border-radius-md);
  box-shadow: var(--neumorphism-flat);
  transition: var(--transition);
  border: var(--border-light);
}

.neumorphic-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
  border-color: rgba(157, 110, 253, 0.3);
}

@media screen and (max-width: 768px) {
  .section {
    padding: 4rem 0;
  }
  
  .section-title {
    font-size: 2rem;
  }
  
  .section-description {
    font-size: 1.1rem;
  }
  
  .button {
    padding: 0.6rem 1.2rem;
    font-size: 0.95rem;
  }
}
</style> 